import { Fragment } from 'react';
import { CheckIcon } from '@heroicons/react/outline';
import clsx from 'clsx';

export type FlowStep = 'orders' | 'review' | 'dispensing' | 'shipping' | 'complete';

const STEPS: { key: FlowStep; label: string }[] = [
  { key: 'orders',     label: 'Orders'     },
  { key: 'review',     label: 'Review'     },
  { key: 'dispensing', label: 'Dispensing' },
  { key: 'shipping',   label: 'Shipping'   },
  { key: 'complete',   label: 'Complete'   },
];

interface Props {
  targetStep: FlowStep;
  fromStep?: FlowStep;
  direction?: 'forward' | 'backward';
}

const OrderFlowTimeline = ({ targetStep, fromStep, direction = 'forward' }: Props) => {
  const targetIndex = STEPS.findIndex(s => s.key === targetStep);
  const fromIndex   = fromStep
    ? STEPS.findIndex(s => s.key === fromStep)
    : direction === 'backward' ? targetIndex + 1 : targetIndex - 1;
  const isBackward  = fromIndex > targetIndex;
  const minIdx      = Math.min(fromIndex, targetIndex);
  const maxIdx      = Math.max(fromIndex, targetIndex);

  return (
    <div className="flex items-start w-full pb-1">
      {STEPS.map((step, i) => {
        const isFrom      = i === fromIndex;
        const isTarget    = i === targetIndex;
        const isPast      = isBackward ? i < targetIndex : i < fromIndex;
        const isBetween   = i > minIdx && i < maxIdx && !isFrom && !isTarget;
        const isFuture    = isBackward ? i > fromIndex  : i > targetIndex;
        const isChecked   = isPast || isBetween;
        const isLinePast  = isBackward ? i < targetIndex : i < fromIndex;
        const isLineAnimated = i >= minIdx && i < maxIdx;

        const showArrow   = isBackward ? i === minIdx : i === maxIdx - 1;
        const arrowhead = showArrow ? (
          <svg width="6" height="10" className="flex-none" style={{ marginTop: '7px' }}>
            {isBackward
              ? <polygon points="6,0 0,5 6,10" fill="#E6CC00" />
              : <polygon points="0,0 6,5 0,10" fill="#E6CC00" />
            }
          </svg>
        ) : null;

        const connector = i < STEPS.length - 1 ? (
          isLineAnimated ? (
            <>
              {isBackward && arrowhead}
              <svg
                className="flex-1 mx-0.5"
                height="10"
                style={{ minWidth: 0, marginTop: '7px' }}
              >
                <defs>
                  <pattern
                    id={`sq-${step.key}`}
                    x="0"
                    y="0"
                    width="20"
                    height="10"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M0,5 C5,1 10,9 20,5"
                      stroke="#E6CC00"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                    />
                    <animateTransform
                      attributeName="patternTransform"
                      type="translate"
                      from="0,0"
                      to={isBackward ? '-20,0' : '20,0'}
                      dur="0.7s"
                      repeatCount="indefinite"
                    />
                  </pattern>
                </defs>
                <rect width="100%" height="10" fill={`url(#sq-${step.key})`} />
              </svg>
              {!isBackward && arrowhead}
            </>
          ) : (
            <div className="flex-1 h-[2px] mt-[10px] mx-0.5 relative rounded-full bg-gray-200">
              {isLinePast && (
                <div className="absolute inset-0 bg-gray-300 rounded-full" />
              )}
            </div>
          )
        ) : null;

        return (
          <Fragment key={step.key}>
            <div className="flex flex-col items-center flex-none">
              <div className="relative w-6 h-6 flex items-center justify-center">
                {isTarget && (
                  <span
                    className="absolute w-6 h-6 rounded-full bg-[#FFFDA5] animate-ping"
                    style={{ opacity: 0.8, animationDelay: '0.55s' }}
                  />
                )}
                <div className={clsx(
                  'relative z-10 w-3 h-3 rounded-full transition-colors flex items-center justify-center',
                  isChecked && 'bg-gray-400',
                  isFrom    && 'bg-gray-400 ring-2 ring-gray-300 ring-offset-1',
                  isTarget  && 'bg-[#FFFDA5] ring-2 ring-[#E6CC00] ring-offset-1',
                  isFuture  && 'bg-gray-200',
                  !isChecked && !isFrom && !isTarget && !isFuture && 'bg-gray-200',
                )}>
                  {isChecked && <CheckIcon className="w-2 h-2 text-white stroke-[3]" />}
                </div>
              </div>
              <span className={clsx(
                'text-xxs text-center leading-tight mt-0.5 whitespace-nowrap',
                isPast     && 'text-gray-400',
                isBetween  && 'text-gray-400',
                isFrom     && 'text-gray-500 font-Sohne-Semibold',
                isTarget   && 'text-black font-Sohne-Semibold',
                isFuture   && 'text-gray-300',
              )}>
                {step.label}
              </span>
            </div>
            {connector}
          </Fragment>
        );
      })}
    </div>
  );
};

export default OrderFlowTimeline;
